import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FleetMap = () => {
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [mapView, setMapView] = useState('emissions'); // emissions, routes, alerts
  const [vessels, setVessels] = useState([]);

  useEffect(() => {
    // Mock vessel data with real-time positions
    const mockVessels = [
      {
        id: 'mv-atlantic-voyager',
        name: 'MV Atlantic Voyager',
        type: 'Container',
        position: { lat: 51.5074, lng: -0.1278 },
        status: 'underway',
        emissions: {
          current: 245.8,
          daily: 2450,
          efficiency: 'B'
        },
        compliance: {
          status: 'compliant',
          rating: 'B',
          riskLevel: 'low'
        },
        route: {
          from: 'London',
          to: 'Hamburg',
          eta: '2025-01-23T14:30:00Z',
          progress: 65
        }
      },
      {
        id: 'mv-pacific-trader',
        name: 'MV Pacific Trader',
        type: 'Bulk Carrier',
        position: { lat: 40.7128, lng: -74.0060 },
        status: 'at-port',
        emissions: {
          current: 0,
          daily: 1850,
          efficiency: 'C'
        },
        compliance: {
          status: 'at-risk',
          rating: 'D',
          riskLevel: 'high'
        },
        route: {
          from: 'New York',
          to: 'Rotterdam',
          eta: '2025-01-25T08:00:00Z',
          progress: 0
        }
      },
      {
        id: 'mv-mediterranean-star',
        name: 'MV Mediterranean Star',
        type: 'Tanker',
        position: { lat: 43.2965, lng: 5.3698 },
        status: 'underway',
        emissions: {
          current: 189.3,
          daily: 2100,
          efficiency: 'A'
        },
        compliance: {
          status: 'compliant',
          rating: 'A',
          riskLevel: 'low'
        },
        route: {
          from: 'Marseille',
          to: 'Barcelona',
          eta: '2025-01-22T16:45:00Z',
          progress: 42
        }
      }
    ];

    setVessels(mockVessels);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'text-success';
      case 'at-risk': return 'text-warning';
      case 'non-compliant': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'underway': return 'Navigation';
      case 'at-port': return 'Anchor';
      case 'maintenance': return 'Wrench';
      default: return 'Ship';
    }
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'A': return 'bg-success text-success-foreground';
      case 'B': return 'bg-accent text-accent-foreground';
      case 'C': return 'bg-warning text-warning-foreground';
      case 'D': case'E': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const handleVesselSelect = (vessel) => {
    setSelectedVessel(vessel);
  };

  const mapViewOptions = [
    { id: 'emissions', label: 'Emissions', icon: 'Zap' },
    { id: 'routes', label: 'Routes', icon: 'Route' },
    { id: 'alerts', label: 'Alerts', icon: 'AlertTriangle' }
  ];

  return (
    <div className="maritime-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Fleet Map</h2>
            <p className="text-sm text-muted-foreground">
              Real-time vessel positions and emissions data
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            {mapViewOptions?.map((option) => (
              <Button
                key={option?.id}
                variant={mapView === option?.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMapView(option?.id)}
                iconName={option?.icon}
                iconPosition="left"
              >
                {option?.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      <div className="relative">
        {/* Map Container */}
        <div className="h-96 bg-muted rounded-lg overflow-hidden">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Fleet Map"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=51.5074,-0.1278&z=4&output=embed"
            className="border-0"
          />
          
          {/* Vessel Overlays */}
          <div className="absolute inset-0 pointer-events-none">
            {vessels?.map((vessel, index) => (
              <div
                key={vessel?.id}
                className={`absolute pointer-events-auto cursor-pointer transform -translate-x-1/2 -translate-y-1/2 ${
                  index === 0 ? 'top-1/3 left-1/4' :
                  index === 1 ? 'top-2/3 left-1/3': 'top-1/2 right-1/3'
                }`}
                onClick={() => handleVesselSelect(vessel)}
              >
                <div className={`p-2 rounded-full shadow-maritime-md transition-maritime ${
                  selectedVessel?.id === vessel?.id 
                    ? 'bg-primary text-primary-foreground scale-110' 
                    : 'bg-card text-foreground hover:scale-105'
                }`}>
                  <Icon name={getStatusIcon(vessel?.status)} size={16} strokeWidth={2} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vessel Details Panel */}
        {selectedVessel && (
          <div className="absolute top-4 right-4 w-80 bg-card border border-border rounded-lg shadow-maritime-lg">
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-foreground">
                    {selectedVessel?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedVessel?.type}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedVessel(null)}
                  className="p-1 hover:bg-muted rounded"
                >
                  <Icon name="X" size={16} strokeWidth={2} />
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <div className="flex items-center space-x-2">
                    <Icon name={getStatusIcon(selectedVessel?.status)} size={14} strokeWidth={2} />
                    <span className="text-sm font-medium capitalize">
                      {selectedVessel?.status?.replace('-', ' ')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">CII Rating</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getRatingColor(selectedVessel?.compliance?.rating)}`}>
                    {selectedVessel?.compliance?.rating}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Current Emissions</span>
                  <span className="text-sm font-medium">
                    {selectedVessel?.emissions?.current} kg CO₂/h
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Route Progress</span>
                  <span className="text-sm font-medium">
                    {selectedVessel?.route?.progress}%
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">
                  {selectedVessel?.route?.from} → {selectedVessel?.route?.to}
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${selectedVessel?.route?.progress}%` }}
                  />
                </div>
              </div>

              <div className="mt-4 flex space-x-2">
                <Button variant="outline" size="sm" className="flex-1">
                  View Details
                </Button>
                <Button variant="default" size="sm" className="flex-1">
                  Optimize Route
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Map Legend */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full" />
              <span>Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning rounded-full" />
              <span>At Risk</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error rounded-full" />
              <span>Non-Compliant</span>
            </div>
          </div>
          <div className="text-right">
            Last updated: {new Date()?.toLocaleTimeString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetMap;